/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { defaultValue } from './SwitchRole';

const SwitchRoleManager: ComponentManagerFn<'SwitchRole'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'SwitchRole'>(platform, 'SwitchRole'),
    type: 'SwitchRole',
    name: 'Switch Role',
    defaultValue,
    props: {},
    category: 'Page Header',
    icon: { iconName: 'user-tag' },
    description: `**Overview**<br/>
The Switch Role component provides a dropdown interface that allows a user to change their active security role within the application. This is particularly useful for administrators or users with multiple assigned roles (e.g., "Manager" vs. "Employee") who need to view the application from different permission perspectives.
**Note:** The standard application header normally includes this control by default. This component is intended for use when building a **Custom Header** where you want to retain this specific security functionality.

**1. Functionality**<br/>
* **Role Selection:** Displays a dynamic list of all authorized roles assigned to the current user account.
* **Context Switching:** Selecting a different role immediately refreshes the application context. This triggers a re-evaluation of permissions, menu visibility, and data access rules associated with the newly selected role.

**2. Configuration**<br/>
* **Zero Configuration:** Like the Page Menu or Dark Mode Toggle, this component accepts no manual properties (props: {}). 
* **Security Integration:** It automatically and securely retrieves the current user's available roles directly from the system's security provider, ensuring users can only switch to roles they are officially granted.

**3. Best Use Cases**<br/>
* **Custom Headers:** Restoring role-switching capabilities in a custom-designed navigation bar to ensure power users maintain full control over their access context.
* **Admin & Support Dashboards:** Placing the control prominently for system administrators who frequently need to test permissions or troubleshoot user issues by viewing the app through different access levels.
* **Multi-Function Users:** Supporting employees who perform distinct sets of tasks (e.g., a user who is both a "Field Technician" and a "Service Dispatcher") and need to switch contexts to access specific pages or data sets.
* **Permission Auditing:** Allowing developers and QA testers to quickly verify that UI elements are correctly hidden or shown based on the active security role.`,
  };
};

export default SwitchRoleManager;
